import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  XCircle,
  ArrowLeft,
  Loader2,
  BookOpen,
  CheckCircle,
  Globe,
  Users,
  Target,
  Tag,
  Sparkles,
  Award,
  TrendingUp,
  Star,
  FileText,
  MapPin,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { learningPathApiService } from '@/services/api/learning-path-api.service';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
import ReviewsList from '@/components/reviews/ReviewsList';
import { ReviewType } from '@/types/review.types';

const tabs = [
  { id: 'overview', label: 'Overview', icon: BookOpen },
  { id: 'courses', label: 'Courses', icon: FileText },
  { id: 'outcomes', label: 'Outcomes', icon: Target },
  { id: 'reviews', label: 'Reviews', icon: Star },
];

export default function LearningPathDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'overview';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Fetch learning path details
  const {
    data: learningPath,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['learning-path-details', id],
    queryFn: () => learningPathApiService.getLearningPathById(id!),
    enabled: !!id,
  });

  // Publish mutation
  const publishMutation = useMutation({
    mutationFn: () => learningPathApiService.publishLearningPath(id!),
    onSuccess: () => {
      toast.success('Learning path published successfully!');
      queryClient.invalidateQueries({ queryKey: ['learning-path-details', id] });
      queryClient.invalidateQueries({ queryKey: ['learning-paths'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to publish learning path');
    },
  });

  // Modal state
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);

  const handleConfirmPublish = () => {
    publishMutation.mutate();
    setIsPublishModalOpen(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="relative w-full h-64 md:h-80 lg:h-96">
          <SkeletonLoader type="custom" className="w-full h-full rounded-none" />
        </div>
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-6 md:py-8">
            <div className="flex-1 space-y-4">
              <SkeletonLoader type="custom" className="h-4 w-36 rounded" />
              <SkeletonLoader type="custom" className="h-8 w-3/4 rounded" />
              <div className="flex gap-4">
                <SkeletonLoader type="custom" className="h-4 w-24 rounded" />
                <SkeletonLoader type="custom" className="h-4 w-24 rounded" />
                <SkeletonLoader type="custom" className="h-4 w-20 rounded" />
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white border-b">
          <div className="container mx-auto px-4">
            <div className="flex gap-6 py-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <SkeletonLoader key={i} type="custom" className="h-5 w-20 rounded" />
              ))}
            </div>
          </div>
        </div>
        <div className="container mx-auto px-4 py-6 md:py-8">
          <div className="grid lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-sm p-6 md:p-8 space-y-4">
                <SkeletonLoader type="title" />
                <SkeletonLoader type="text" count={3} />
              </div>
            </div>
            <div>
              <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
                <SkeletonLoader type="custom" className="h-5 w-28 rounded" />
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <SkeletonLoader type="custom" className="h-5 w-5 rounded-full flex-shrink-0" />
                    <div className="flex-1 space-y-1">
                      <SkeletonLoader type="custom" className="h-4 w-24 rounded" />
                      <SkeletonLoader type="custom" className="h-3 w-16 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !learningPath) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-4">
            <XCircle className="w-10 h-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Learning Path Not Found</h2>
          <p className="text-gray-600 mb-6">
            The learning path you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={() => navigate('/admin/learning-paths')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Learning Paths
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Banner Image Section */}
      <div className="relative w-full h-64 md:h-80 lg:h-96 bg-gradient-to-r from-purple-600 to-pink-600">
        {learningPath.banner_image_url || learningPath.thumbnail_image_url ? (
          <img
            src={learningPath.banner_image_url || learningPath.thumbnail_image_url}
            alt={learningPath.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <MapPin className="w-24 h-24 md:w-32 md:h-32 text-white/30" />
          </div>
        )}

        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

        {/* Back Button */}
        <div className="absolute top-4 left-4 z-10">
          <button
            onClick={() => navigate('/admin/learning-paths')}
            className="flex items-center gap-2 px-4 py-2 bg-black/30 backdrop-blur-sm text-white rounded-lg hover:bg-black/50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 md:w-5 md:h-5" />
            <span className="hidden sm:inline">Back to Learning Paths</span>
          </button>
        </div>

        {/* Status + Publish */}
        <div className="absolute top-4 right-4 z-10 flex gap-2">
          <span className={`text-xs md:text-sm px-3 py-1 rounded-full font-medium ${
            learningPath.status === 'PUBLISHED' ? 'bg-green-500 text-white' :
            learningPath.status === 'DRAFT' ? 'bg-yellow-500 text-white' :
            'bg-gray-500 text-white'
          }`}>
            {learningPath.status}
          </span>
          {learningPath.status === 'DRAFT' && (
            <button
              onClick={() => setIsPublishModalOpen(true)}
              disabled={publishMutation.isPending}
              className="text-xs md:text-sm px-3 py-1 rounded-full font-medium bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {publishMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin inline" />
              ) : (
                <><Globe className="w-3 h-3 inline mr-1" />Publish</>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Title and Info Section */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-6 md:py-8">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 lg:gap-8">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-5 h-5 md:w-6 md:h-6 text-purple-600" />
                <span className="text-xs md:text-sm font-medium uppercase tracking-wider text-purple-600">Learning Path</span>
                {learningPath.difficulty_progression && (
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                    {learningPath.difficulty_progression}
                  </span>
                )}
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3">{learningPath.title}</h1>

              {/* Rating */}
              <div className="inline-flex items-center gap-1.5 mb-3">
                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                <span className="font-medium text-gray-700">{learningPath.rating?.toFixed(1) || '0.0'}</span>
                <span className="text-gray-500 text-sm">({learningPath.review_count || 0} reviews)</span>
              </div>

              {/* Quick Stats */}
              <div className="flex flex-wrap gap-4 md:gap-6">
                <div className="flex items-center gap-2 text-sm md:text-base text-gray-600">
                  <BookOpen className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />
                  <span>{learningPath.total_courses || 0} courses</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b sticky top-0 z-10 shadow-sm overflow-x-auto">
        <div className="container mx-auto px-4">
          <div className="flex gap-4 md:gap-8 min-w-max md:min-w-0">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-3 md:px-4 py-3 md:py-4 border-b-2 font-medium transition-colors text-sm md:text-base whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <tab.icon className="w-4 h-4 md:w-5 md:h-5" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-6 md:py-8">
        <div className="grid lg:grid-cols-3 gap-6 md:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 order-2 lg:order-1">

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Description */}
                <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 lg:p-8">
                  <h2 className="text-xl md:text-2xl font-bold mb-4 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 md:w-6 md:h-6 text-purple-600" />
                    About This Learning Path
                  </h2>
                  <div className="prose prose-sm md:prose-base max-w-none">
                    <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                      {learningPath.description || 'No description available for this learning path.'}
                    </p>
                  </div>

                  {/* Info Grid */}
                  <div className="mt-6 md:mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                    {learningPath.target_role_or_skill && (
                      <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                        <div className="flex items-center gap-2 mb-2">
                          <Target className="w-4 h-4 text-purple-600" />
                          <p className="text-xs text-gray-600 font-medium">Target Role</p>
                        </div>
                        <p className="text-sm font-semibold text-gray-900">{learningPath.target_role_or_skill}</p>
                      </div>
                    )}
                    {learningPath.targeted_audience && (
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                        <div className="flex items-center gap-2 mb-2">
                          <Users className="w-4 h-4 text-blue-600" />
                          <p className="text-xs text-gray-600 font-medium">Target Audience</p>
                        </div>
                        <p className="text-sm font-semibold text-gray-900">{learningPath.targeted_audience}</p>
                      </div>
                    )}
                    <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                      <div className="flex items-center gap-2 mb-2">
                        <BookOpen className="w-4 h-4 text-green-600" />
                        <p className="text-xs text-gray-600 font-medium">Total Courses</p>
                      </div>
                      <p className="text-sm font-semibold text-gray-900">{learningPath.total_courses || 0} courses</p>
                    </div>
                  </div>
                </div>


                {/* AI Suggestions */}
                {learningPath.suggestions && learningPath.suggestions.length > 0 && (
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border-2 border-blue-200 p-4 md:p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-5 h-5 text-blue-600" />
                      <h3 className="text-lg font-bold text-gray-900">AI Suggestions</h3>
                    </div>
                    <ul className="space-y-2">
                      {learningPath.suggestions.map((suggestion, index) => (
                        <li key={index} className="flex items-start gap-2 text-gray-700 text-sm">
                          <span className="text-blue-600 font-bold mt-0.5">•</span>
                          <span>{suggestion}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Courses Tab */}
            {activeTab === 'courses' && (
              <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 lg:p-8">
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <h2 className="text-xl md:text-2xl font-bold flex items-center gap-2">
                    <FileText className="w-5 h-5 md:w-6 md:h-6 text-purple-600" />
                    Course Sequence
                  </h2>
                  <span className="text-sm text-gray-500">{learningPath.courses?.length || 0} courses</span>
                </div>
                <p className="text-sm md:text-base text-gray-600 mb-6">Courses are ordered sequentially for structured learning progression</p>

                {learningPath.courses && learningPath.courses.length > 0 ? (
                  <div className="space-y-4">
                    {learningPath.courses
                      .sort((a, b) => a.order - b.order)
                      .map((course, index) => (
                        <div
                          key={course.course_id}
                          className="border-2 border-gray-200 rounded-lg p-3 md:p-5 hover:shadow-md hover:border-purple-200 transition-all"
                        >
                          <div className="flex items-start gap-3 md:gap-5">
                            {/* Order Number */}
                            <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center text-white font-bold text-base md:text-lg">
                              {index + 1}
                            </div>

                            {/* Course Content */}
                            <div className="flex-1 min-w-0">
                              <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-1">
                                {course.course_title}
                              </h3>
                              {course.course_description && (
                                <p className="text-xs md:text-sm text-gray-600 mb-3 line-clamp-2">
                                  {course.course_description}
                                </p>
                              )}
                              <div className="flex flex-wrap items-center gap-3">
                                {course.difficulty_level && (
                                  <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full font-medium capitalize">
                                    {course.difficulty_level}
                                  </span>
                                )}
                                {course.rationale && (
                                  <p className="text-xs text-gray-500 italic">
                                    <Sparkles className="w-3 h-3 inline mr-1 text-blue-500" />
                                    {course.rationale}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <BookOpen className="w-12 h-12 md:w-16 md:h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Courses Added</h3>
                    <p className="text-sm text-gray-600">No courses have been added to this learning path yet.</p>
                  </div>
                )}
              </div>
            )}

            {/* Outcomes Tab */}
            {activeTab === 'outcomes' && (
              <div className="space-y-6">
                {/* Learning Outcomes */}
                <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 lg:p-8">
                  <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 flex items-center gap-2">
                    <Award className="w-5 h-5 md:w-6 md:h-6 text-purple-600" />
                    Learning Outcomes
                  </h2>

                  {learningPath.outcomes && learningPath.outcomes.length > 0 ? (
                    <ul className="space-y-3">
                      {learningPath.outcomes.map((outcome, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-sm md:text-base text-gray-700">{outcome}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Award className="w-12 h-12 md:w-16 md:h-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-sm md:text-base">No learning outcomes defined for this learning path.</p>
                    </div>
                  )}
                </div>

                {/* Tags */}
                {learningPath.tags && learningPath.tags.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 lg:p-8">
                    <h2 className="text-xl md:text-2xl font-bold mb-4 flex items-center gap-2">
                      <Tag className="w-5 h-5 md:w-6 md:h-6 text-purple-600" />
                      Topics Covered
                    </h2>
                    <div className="flex flex-wrap gap-2">
                      {learningPath.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-4 py-2 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 rounded-full text-sm font-medium border border-purple-200"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Reviews Tab */}
            {activeTab === 'reviews' && (
              <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 lg:p-8">
                <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 flex items-center gap-2">
                  <Star className="w-5 h-5 md:w-6 md:h-6 text-yellow-500" />
                  Reviews
                </h2>
                <ReviewsList
                  targetId={id || ''}
                  targetType={ReviewType.LEARNING_PATH}
                  targetTitle={learningPath.title || ''}
                  showWriteReview={false}
                  isAdmin={true}
                />
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="order-1 lg:order-2">
            <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 lg:sticky lg:top-24">
              <h3 className="font-semibold text-base md:text-lg mb-4">Path Summary</h3>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <BookOpen className="w-4 h-4 md:w-5 md:h-5 text-purple-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm md:text-base text-gray-900">Total Courses</p>
                    <p className="text-xs md:text-sm text-gray-600">{learningPath.total_courses || 0} courses</p>
                  </div>
                </div>


                {learningPath.target_role_or_skill && (
                  <div className="flex items-start gap-3">
                    <Target className="w-4 h-4 md:w-5 md:h-5 text-purple-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm md:text-base text-gray-900">Target Role</p>
                      <p className="text-xs md:text-sm text-gray-600">{learningPath.target_role_or_skill}</p>
                    </div>
                  </div>
                )}

                {learningPath.difficulty_progression && (
                  <div className="flex items-start gap-3">
                    <Award className="w-4 h-4 md:w-5 md:h-5 text-purple-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm md:text-base text-gray-900">Difficulty</p>
                      <p className="text-xs md:text-sm text-gray-600">{learningPath.difficulty_progression}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Publish Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isPublishModalOpen}
        onClose={() => setIsPublishModalOpen(false)}
        onConfirm={handleConfirmPublish}
        title="Publish Learning Path"
        message="Are you sure you want to publish this learning path? It will be visible to learners."
        confirmButtonText="Publish"
        isLoading={publishMutation.isPending}
      />
    </div>
  );
}
